import { useEffect, useRef, useState } from 'react'
import queryClient from 'api/queryClient'
import threadReplyDelta, {
    threadAnswerTotal,
    threadReplyBaselineIsReady,
    threadVisibleNewReplyCount,
} from './threadNewReplyState'

interface CachedQuestShape {
    sorted_answers?: Array<{ user_id?: unknown }>
    sorted_answer_ids?: unknown[]
    __knovAnswerHydrationScope?: 'stream-preview' | 'full'
}

function readQuest(questId: string): CachedQuestShape | undefined {
    return (queryClient.getQueryData(['quest', questId]) ||
        queryClient.getQueryData(['quest', String(questId)])) as CachedQuestShape | undefined
}

// Counts posts appended to the open thread while the viewer is away from the
// bottom (docs/feed-new-items-pill.md). No socket plumbing of its own:
// useQuestChannel already live-updates the thread's ['quest', id] cache entry;
// we just watch the cache. Reaching the bottom resets the baseline.
export default function useThreadNewReplies(
    questId: string | null | undefined,
    atBottom: boolean,
): number {
    const [newReplies, setNewReplies] = useState(0)
    const baselineRef = useRef<number | null>(null)
    const baselineReadyRef = useRef(false)
    const atBottomRef = useRef(atBottom)
    atBottomRef.current = atBottom

    useEffect(() => {
        if (!questId) {
            baselineReadyRef.current = false
            setNewReplies(0)
            return
        }

        const id = String(questId)
        const initialQuest = readQuest(id)
        baselineRef.current = initialQuest ? threadAnswerTotal(initialQuest) : null
        baselineReadyRef.current = Boolean(initialQuest && threadReplyBaselineIsReady(initialQuest))
        setNewReplies(0)

        const unsubscribe = queryClient.getQueryCache().subscribe(event => {
            if (event?.type !== 'updated') return
            const key = (event as { query?: { queryKey?: unknown } }).query?.queryKey
            if (!Array.isArray(key) || key[0] !== 'quest' || String(key[1]) !== id) return

            const quest = readQuest(id)
            if (!quest) return
            const total = threadAnswerTotal(quest)

            if (!baselineReadyRef.current) {
                baselineRef.current = total
                baselineReadyRef.current = threadReplyBaselineIsReady(quest)
                setNewReplies(0)
                return
            }

            const delta = threadReplyDelta(baselineRef.current, total, atBottomRef.current)
            baselineRef.current = delta.baselineTotal

            if (delta.reset) {
                // First payload, following along live, or posts were removed:
                // establish a fresh baseline without counting existing posts.
                setNewReplies(0)
                return
            }

            const appended = delta.appended
            if (appended <= 0) return

            const me = String(gon?.currentUser?.id || '')
            setNewReplies(threadVisibleNewReplyCount(quest, appended, me))
        })

        return unsubscribe
    }, [questId])

    // Reaching the bottom consumes the pending count.
    useEffect(() => {
        if (!questId || !atBottom) return
        const quest = readQuest(String(questId))
        baselineRef.current = quest ? threadAnswerTotal(quest) : null
        baselineReadyRef.current = Boolean(quest && threadReplyBaselineIsReady(quest))
        setNewReplies(0)
    }, [atBottom, questId])

    return newReplies
}
